import type { PrototyperMessageFields } from '@ideoz/core';
import { planMessageHasPersistedBuild } from '@ideoz/core';
import type { WorkbenchSnapshotBootstrap } from '../usePrototyperWorkbenchFacade';

export type BuildStreamStartDecision = 'start' | 'skip' | 'wait' | 'resume-build';

export type SnapshotIndexEntry = {
  llmInteractionId: string;
  snapshotId: string;
  revision: number;
  buildVerified?: boolean;
  createdAt?: string;
  prototyperDemoLinkId?: string;
};

/**
 * Whether to POST …/build-stream for a prototyper plan turn.
 */
export function shouldStartBuildStream(args: {
  planLlmInteractionId: string;
  builtTurnIds: ReadonlySet<string>;
  bootstrap: WorkbenchSnapshotBootstrap;
  planMessage?: PrototyperMessageFields | null;
  snapshotIndex?: ReadonlyMap<string, SnapshotIndexEntry>;
  getSnapshotIndexEntry?: (llmInteractionId: string) => SnapshotIndexEntry | undefined;
  snapshotIndexLoading?: boolean;
}): BuildStreamStartDecision {
  const {
    planLlmInteractionId,
    builtTurnIds,
    bootstrap,
    planMessage,
    snapshotIndex,
    getSnapshotIndexEntry,
    snapshotIndexLoading,
  } = args;

  if (builtTurnIds.has(planLlmInteractionId)) {
    return 'skip';
  }

  const indexed =
    getSnapshotIndexEntry?.(planLlmInteractionId) ??
    snapshotIndex?.get(planLlmInteractionId);
  if (indexed?.buildVerified === true) {
    return 'skip';
  }

  // Both waits come before the resume/skip branches below: while the index is still loading,
  // a missing entry is indistinguishable from "this turn was never snapshotted", and a resume
  // must never race the bootstrap restore for ownership of the container.
  if (snapshotIndexLoading) {
    return 'wait';
  }

  if (bootstrap === 'pending') {
    return 'wait';
  }

  // The turn's files were snapshotted at build-start but the build never got verified — the
  // build was interrupted (refresh/closed tab). Finish it locally instead of burning a whole
  // LLM turn re-generating code we already have.
  if (indexed != null) {
    return 'resume-build';
  }

  // Deliberately last: a turn with a persisted build but no snapshot at all predates
  // build-start snapshots (or its upload failed). There is nothing to resume from, so keep
  // the historical behavior and leave it alone rather than restarting it.
  if (planMessage && planMessageHasPersistedBuild(planMessage)) {
    return 'skip';
  }

  return 'start';
}
